import type { ImageWidget } from "~/types/widgets";
import { Picture } from "~/components/ui/Picture";

/**
 * @titleBy alt
 */
export interface Banner {
  srcMobile?: ImageWidget;
  srcDesktop?: ImageWidget;
  /**
   * @description Video URL for mobile (optional)
   */
  videoMobile?: string;
  /**
   * @description Video URL for desktop (optional)
   */
  videoDesktop?: string;
  /**
   * @description Media type
   */
  mediaType: "image" | "video";
  /**
   * @description Image alt text
   */
  alt: string;
  /**
   * @description When you click you go to
   */
  href: string;
}

export type BorderRadius =
  | "none"
  | "sm"
  | "md"
  | "lg"
  | "xl"
  | "2xl"
  | "3xl"
  | "full";

/**
 * Named interfaces (not inline object literals): the schema generator keys a
 * prop to loaders by the type's SYMBOL name. Anonymous inline objects all
 * resolve to the symbol name `__type`, which collides with the anonymous
 * return types of loaders (categoryBanner, workWithUs, PLPDefaultPath) — the
 * generator then replaced the inline form with a loader picker. Giving these
 * types real interface names breaks that collision so the admin renders the
 * proper mobile/desktop selects (see generate-schema.ts block-ref detection).
 */
export interface ItemsPerLine {
  /** @default 2 */
  mobile: 1 | 2;
  /** @default 4 */
  desktop: 1 | 2 | 3 | 4 | 6 | 8;
}

export interface BannerBorderRadius {
  /** @default none */
  mobile: BorderRadius;
  /** @default none */
  desktop: BorderRadius;
}

export interface Props {
  hasTitle?: boolean;

  title?: string;
  /**
   * @description Default is 2 for mobile and all for desktop
   */
  itemsPerLine: ItemsPerLine;
  /**
   * @description Item's border radius in px
   */
  borderRadius: BannerBorderRadius;
  /**
   * @description Add lateral padding when needed
   * @default true
   */
  hasPadding?: boolean;
  banners: Banner[];
}

const DESKTOP_COLUMNS = {
  1: "sm:grid-cols-1",
  2: "sm:grid-cols-2",
  3: "sm:grid-cols-3",
  4: "sm:grid-cols-4",
  6: "sm:grid-cols-6",
  8: "sm:grid-cols-8",
};

const RADIUS_MOBILE = {
  none: "rounded-none",
  sm: "rounded-sm",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  "2xl": "rounded-2xl",
  "3xl": "rounded-3xl",
  full: "rounded-full",
};

const RADIUS_DESKTOP = {
  none: "sm:rounded-none",
  sm: "sm:rounded-sm",
  md: "sm:rounded-md",
  lg: "sm:rounded-lg",
  xl: "sm:rounded-xl",
  "2xl": "sm:rounded-2xl",
  "3xl": "sm:rounded-3xl",
  full: "sm:rounded-full",
};

/**
 * Deriva largura/altura-alvo de resize e o `sizes` do `<picture>` a partir
 * do número real de colunas desktop do grid — sem isso, qualquer grid de
 * 2+ colunas serve uma imagem maior que a caixa real (o bug que gerou o
 * desperdício medido no PSI: banner de 1280px numa caixa de ~300-450px).
 */
export function getBannerGridDesktopImageDimensions(desktopCols: number): {
  width: number;
  height: number;
  sizes: string;
} {
  const width = Math.round(1920 / desktopCols);
  const height = Math.round(width * (720 / 1280));
  const sizes = `${Math.round(100 / desktopCols)}vw`;
  return { width, height, sizes };
}

const DEFAULT_PROPS: Props = {
  title: "Summer bags",
  hasPadding: false,
  banners: [
    {
      alt: "a",
      href: "a",
      mediaType: "image",
      srcMobile:
        "https://decoims.com/danielatombini/b2d1e3c8-79ed-4a9e-b000-bd039f109b2f/82727553_82727553-f670-4e7c-b9c2-9452aed1955f.jpg",
      srcDesktop:
        "https://decoims.com/danielatombini/f34a0183-6226-4a0b-8abf-742ffe84bd64/7b3a9d75_7b3a9d75-57a5-43cf-a3c5-f689a997f24e.jpg",
    },
    {
      alt: "a",
      href: "a",
      mediaType: "image",
      srcMobile:
        "https://decoims.com/danielatombini/c3cafe2e-2da6-4d8d-b5eb-da63a03e7b91/c5c6bdf6_c5c6bdf6-5555-488c-8b14-719e4158dea6.jpg",
      srcDesktop:
        "https://decoims.com/danielatombini/cf562f4b-a594-4fbe-b8ac-070305adc8e3/3e2b7824_3e2b7824-d75c-4704-8d32-621bfc9b20cf.jpg",
    },
  ],
  borderRadius: {
    mobile: "3xl",
    desktop: "3xl",
  },
  itemsPerLine: {
    mobile: 2,
    desktop: 2,
  },
};

function BannerGrid(props: Props) {
  const {
    title,
    hasTitle,
    itemsPerLine,
    borderRadius,
    hasPadding,
    banners = [],
  } = { ...DEFAULT_PROPS, ...props };

  const desktopImage = getBannerGridDesktopImageDimensions(itemsPerLine?.desktop ?? 4);

  return (
    <section className={`w-full mx-auto px-0 ${hasPadding ? "px-4 lg:px-8" : ""}`}>
      {hasTitle && (
        <div className="py-6 md:py-0 md:pb-10 flex items-center mt-6">
          <h2 className="text-lg leading-5 font-semibold uppercase">{title}</h2>

          <div className="h-[1px] w-full ml-4"></div>
        </div>
      )}
      <div
        className={`flex flex-col gap-4 md:grid md:gap-6 mt-2 lg:mt-0 ${
          DESKTOP_COLUMNS[itemsPerLine?.desktop ?? 4]
        }`}
      >
        {banners.map((
          {
            href,
            srcMobile,
            srcDesktop,
            videoMobile,
            videoDesktop,
            mediaType,
            alt,
          },
        ) => {
          const mobileSrc = srcMobile ?? srcDesktop;
          const desktopSrc = srcDesktop ?? srcMobile;

          return (
            <a
              href={href}
              className={`block overflow-hidden group ${
                RADIUS_MOBILE[borderRadius.mobile ?? "none"]
              } ${RADIUS_DESKTOP[borderRadius.desktop ?? "none"]}`}
            >
              {mediaType === "video"
                ? (
                  // Um único <video> com duas <source media="...">, em vez
                  // de dois <video> escondidos por CSS (`block md:hidden`/
                  // `hidden md:block`): `display:none` não impede o
                  // navegador de baixar um <video autoPlay>, só esconde
                  // visualmente — as DUAS variantes (mobile e desktop)
                  // baixavam por completo em toda visita.
                  <video
                    aria-label={alt}
                    className="w-full group-hover:brightness-[0.8] transition-all duration-300 object-cover"
                    autoPlay
                    muted
                    loop
                    playsInline
                    poster={mobileSrc ?? desktopSrc}
                  >
                    {(videoDesktop || videoMobile) && (
                      <source
                        media="(min-width: 768px)"
                        src={videoDesktop || videoMobile}
                        type="video/mp4"
                      />
                    )}
                    {(videoMobile || videoDesktop) && (
                      <source src={videoMobile || videoDesktop} type="video/mp4" />
                    )}
                    Seu navegador não suporta vídeos.
                  </video>
                )
                : (
                  <Picture
                    sources={[
                      {
                        media: "(max-width: 767px)",
                        // Sem `height` fixa: mesma correção de
                        // BannerCarousel.tsx — a caixa antiga (750x500,
                        // paisagem) descasava da proporção real das artes
                        // mobile (~750x1001, retrato), cortando quase
                        // metade da altura, geralmente onde fica o texto
                        // (docs/superpowers/specs/2026-08-12-home-fold-crop-fixes-design.md).
                        src: mobileSrc ?? "",
                        width: 750,
                        sizes: "100vw",
                      },
                      {
                        media: "(min-width: 768px)",
                        src: desktopSrc ?? "",
                        // Sem `height` fixa: banners de 2+ colunas têm arte
                        // desktop que já bate com a suposição de 16:9 desta
                        // função (por isso nunca cortava), mas banners de
                        // coluna única usam um template mais largo (~2.51:1,
                        // "cinematic") — a mesma arte em duas campanhas
                        // diferentes ("under the sun", "winter sale") — que
                        // a caixa fixa 16:9 cortava nas laterais. Deixar o
                        // CDN preservar a proporção real resolve os dois
                        // casos sem depender da contagem de colunas
                        // (docs/superpowers/specs/2026-08-12-home-fold-crop-fixes-design.md,
                        // apêndice pós-fechamento).
                        width: desktopImage.width,
                        sizes: desktopImage.sizes,
                      },
                    ]}
                    className="w-full group-hover:brightness-[0.8] transition-all duration-300 aspect-[0.75_auto] md:aspect-auto"
                    loading="lazy"
                    src={mobileSrc ?? ""}
                    alt={alt}
                    width={750}
                  />
                )}
            </a>
          );
        })}
      </div>
    </section>
  );
}

export default BannerGrid;
